import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table } from '@/components/ui/table';
import { Spinner } from '@/components/ui/Spinner';
import type { Database } from '@/integrations/supabase/types';
import { toast } from 'sonner';
import { useAuth } from '@/lib/context/AuthContext';
import { getHotelData } from '@/lib/services/hotelService';
import { HotelData } from '@/lib/types/auth';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { format, subDays, parseISO, eachDayOfInterval } from 'date-fns';

type ChatLog = Database['public']['Tables']['chat_logs']['Row'];
type Hotel = Database['public']['Tables']['hotels']['Row'];
type ChatLogDisplay = Pick<ChatLog, 'id' | 'question' | 'answer' | 'created_at' | 'session_id'>;

interface AnalyticsData {
  totalConversations: number;
  totalMessages: number;
  dailyVolume: Array<{ date: string; count: number }>;
  peakHours: Array<{ hour: number; count: number }>;
  uniqueDevices: number;
  topQuestions: Array<{ question: string; count: number }>;
  answerRate: { answered: number; unanswered: number };
  unansweredQuestions: Array<ChatLogDisplay>;
  recentLogs: Array<ChatLogDisplay>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hotelInfo, setHotelInfo] = useState<HotelData | null>(null);
  const { auth, supabase } = useAuth();

  useEffect(() => {
    const loadHotelData = async () => {
      if (auth.user) {
        try {
          const data = await getHotelData();
          if (data) {
            setHotelInfo(data);
          }
        } catch (error) {
          console.error('Error loading hotel data:', error);
          toast.error('Failed to load hotel data');
        }
      }
    };

    loadHotelData();
  }, [auth.user]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!auth.user) {
          throw new Error('User not authenticated');
        }

        // Get hotel ID for the current user
        const { data: hotel, error: hotelError } = await supabase
          .from('hotels')
          .select('id')
          .eq('owner_id', auth.user.id)
          .single<Pick<Hotel, 'id'>>();

        if (hotelError) {
          if (import.meta.env.MODE === 'development') {
            console.error('Error fetching hotel:', hotelError);
          }
          throw new Error('Failed to fetch hotel data');
        }

        if (!hotel) {
          throw new Error('No hotel found for this user');
        }

        // Fetch all analytics data in parallel
        const [
          { count: totalMessages },
          { count: uniqueDevices },
          { data: dailyVolume },
          { data: peakHours },
          { data: topQuestions },
          { data: unansweredQuestions },
          { data: recentLogs }
        ] = await Promise.all([
          // Total messages
          supabase
            .from('chat_logs')
            .select('*', { count: 'exact', head: true })
            .eq('hotel_id', hotel.id),

          // Unique devices (sessions)
          supabase
            .from('chat_logs')
            .select('session_id', { count: 'exact', head: true })
            .eq('hotel_id', hotel.id),

          // Daily volume (last 30 days)
          supabase
            .from('chat_logs')
            .select('created_at')
            .eq('hotel_id', hotel.id)
            .gte('created_at', subDays(new Date(), 30).toISOString())
            .order('created_at', { ascending: true }),

          // Peak hours
          supabase
            .from('chat_logs')
            .select('created_at')
            .eq('hotel_id', hotel.id),

          // Top questions
          supabase
            .from('chat_logs')
            .select('question')
            .eq('hotel_id', hotel.id)
            .order('created_at', { ascending: false }),

          // Unanswered questions
          supabase
            .from('chat_logs')
            .select('id, question, answer, created_at, session_id')
            .eq('hotel_id', hotel.id)
            .ilike('answer', '%don\'t have that information%')
            .order('created_at', { ascending: false })
            .limit(5),

          // Recent logs
          supabase
            .from('chat_logs')
            .select('id, question, answer, created_at, session_id')
            .eq('hotel_id', hotel.id)
            .order('created_at', { ascending: false })
            .limit(20)
        ]);

        // Process daily volume data
        const dailyVolumeData = dailyVolume?.reduce((acc, log) => {
          const date = format(parseISO(log.created_at), 'MMM dd');
          acc[date] = (acc[date] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        // Generate all 30 days with zero values for missing days
        const endDate = new Date();
        const startDate = subDays(endDate, 29); // 30 days total (including today)
        
        const allDays = eachDayOfInterval({ start: startDate, end: endDate });
        const formattedDailyVolume = allDays.map(day => {
          const dateKey = format(day, 'MMM dd');
          return {
            date: dateKey,
            count: dailyVolumeData?.[dateKey] || 0
          };
        });

        // Process peak hours data
        const peakHoursData = peakHours?.reduce((acc, log) => {
          const hour = new Date(log.created_at).getHours();
          acc[hour] = (acc[hour] || 0) + 1;
          return acc;
        }, {} as Record<number, number>);

        const formattedPeakHours = Object.entries(peakHoursData || {})
          .map(([hour, count]) => ({
            hour: parseInt(hour),
            count
          }))
          .sort((a, b) => a.hour - b.hour);

        // Process top questions
        const questionCounts = topQuestions?.reduce((acc, log) => {
          acc[log.question] = (acc[log.question] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        const formattedTopQuestions = Object.entries(questionCounts || {})
          .map(([question, count]) => ({
            question,
            count
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        // Fetch answer rate from chat_logs using success field
        const { data: successLogs, error: successError } = await supabase
        .from('chat_logs')
        .select('success')
        .eq('hotel_id', hotel.id);

        if (successError) {
        throw new Error('Failed to fetch answer rate data');
        }

        const totalAnswered = successLogs?.filter(log => log.success === true).length || 0;
        const totalUnanswered = successLogs?.filter(log => log.success === false).length || 0;

        setAnalyticsData({
            totalConversations: new Set(recentLogs.map(log => log.session_id)).size || 0,
            totalMessages: totalMessages || 0,
            dailyVolume: formattedDailyVolume,
            peakHours: formattedPeakHours,
            uniqueDevices: new Set(recentLogs.map(log => log.session_id)).size || 0,
            topQuestions: formattedTopQuestions,
            answerRate: {
              answered: totalAnswered,
              unanswered: totalUnanswered
            },
            unansweredQuestions: unansweredQuestions || [],
            recentLogs: recentLogs || []
          });
      } catch (err) {
        if (import.meta.env.MODE === 'development') {
          console.error('Error in fetchAnalytics:', err);
        }
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        toast.error('Failed to load analytics data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [auth.user, supabase]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Spinner size="lg" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Card className="p-4 text-center">
            <p className="text-red-500">{error}</p>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (!analyticsData) {
    return null;
  }

  // Show only start and end date as X axis labels
  const xTicks = [
    analyticsData.dailyVolume[0]?.date,
    analyticsData.dailyVolume[analyticsData.dailyVolume.length - 1]?.date
  ];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <h1 className="text-2xl font-bold">Analytics Dashboard</h1>

        {/* Usage Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className='hidden'>
            <CardHeader>
              <CardTitle className="text-sm">Total Conversations</CardTitle>
            </CardHeader>
            <CardContent className='flex justify-center items-center'>
              <p className="text-3xl font-bold">{analyticsData.totalConversations}</p>
            </CardContent>
          </Card>
          <Card className='border-slate-100 bg-green-500 text-white'>
            <CardContent className='flex justify-center items-center text-center h-full'>
              <div>
              <p className="text-3xl font-bold">{analyticsData.totalMessages}</p>
              <p className='text-sm opacity-80'>Total Messages</p>
              </div>
            </CardContent>
          </Card>
          <Card className='border-slate-100 bg-hotel-secondary text-white'>
            <CardContent className='flex justify-center items-center text-center h-full'>
              <div>
              <p className="text-3xl font-bold">{analyticsData.uniqueDevices}</p>
              <p className='text-sm opacity-80'>Total Guests</p>
              </div>
            </CardContent>
          </Card>
          <Card className='col-span-2 bg-hotel-primary text-white'>
            <CardHeader>
              <CardTitle className="text-sm font-medium opacity-80">Answer Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Answered', value: analyticsData.answerRate.answered },
                        { name: 'Unanswered', value: analyticsData.answerRate.unanswered }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={50}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      <Cell fill="#00C49F" />
                      <Cell fill="#FF8042" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className='bg-slate-50 border-none text-black'>
            <CardHeader>
              <CardTitle className="text-sm">Daily Message Volume</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData.dailyVolume}>
                    <CartesianGrid strokeDasharray="0 3" />
                    <XAxis dataKey="date" ticks={xTicks} tickFormatter={(date) => date} />
                    <YAxis />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'white', color: 'black', border: '0' }}
                      itemStyle={{ color: 'black' }}
                      labelStyle={{ color: 'black' }}
                    />
                    <Bar dataKey="count" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          <Card className='bg-slate-50 border-none text-black'>
            <CardHeader>
              <CardTitle className="text-sm">Peak Hours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData.peakHours}>
                    <CartesianGrid strokeDasharray="0 3" />
                    <XAxis 
                      dataKey="hour" 
                      tickFormatter={(hour) => `${hour}:00`}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(hour) => `${hour}:00`}
                      contentStyle={{ backgroundColor: 'white', color: 'black', border: '0' }}
                      itemStyle={{ color: 'black' }}
                      labelStyle={{ color: 'black' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#4299e1" 
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Questions */}
        <Card className='border-none bg-slate-50'>
          <CardHeader>
            <CardTitle className="text-sm p-0">Top 5 Most Asked Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analyticsData.topQuestions.map((item, index) => (
                <div key={index} className="flex justify-between border-b items-center p-2 bg-gray-50 rounded">
                  <span className="text-sm">{item.question}</span>
                  <span className="text-sm font-medium">{item.count} times</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Unanswered Questions */}
        {analyticsData.unansweredQuestions.length > 0 && (
          <Card className='border-none bg-slate-50'>
            <CardHeader>
              <CardTitle className="text-sm p-0">Recent Unanswered Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className='text-left'>
                  <thead>
                    <tr>
                      <th className='p-2'>Question</th>
                      <th className='p-2'>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.unansweredQuestions.map((log) => (
                      <tr key={log.id}>
                        <td className='align-top border-t p-2'>{log.question}</td>
                        <td className='align-top border-t p-2'>{new Date(log.created_at).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true,
                        })}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
        </div>

        {/* Recent Logs */}
        <Card className='border-none'>
          <CardHeader className='p-0'>
            <CardTitle className="text-sm p-0">Recent Chat Logs</CardTitle>
          </CardHeader>
          <CardContent className='p-0'>
            <div className="overflow-x-auto">
              <Table className='table border-none text-left'>
                <thead>
                  <tr>
                    <th className='p-2 text-xs font-medium'>Question</th>
                    <th className='p-2 text-xs font-medium'>Answer</th>
                    <th className='p-2 text-xs font-medium'>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsData.recentLogs.map((log) => (
                    <tr key={log.id}>
                      <td className='align-top border p-2'>{log.question}</td>
                      <td className='align-top border p-2'>{log.answer}</td>
                      <td className='align-top border p-2'>{new Date(log.created_at).toLocaleString('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
})}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Analytics; 